// ─── What: chart-wide constants and small shared types ────────────────────────
//
// Lifted unchanged from ConvergenceChart. Panel geometry lives in layout.ts;
// everything here is display vocabulary rather than measurement.

import type { OHLCVPoint } from '../../../api/types';
import { autoResolutionMinutes } from '../../../../../data/seriesUtils';

// ─── Public types ─────────────────────────────────────────────────────────────

export interface MarketLineMeta {
    conditionId: string;
    question: string;
    color: string;
    outcomes?: string[] | null;
}

export type CandleResolution = number;

export type ConvictionMetric =
    | 'net_capital'
    | 'net_traders'
    | 'raw_implied'
    | 'open_shares_net';

/** The display mode after gating; `chartType` before it. */
export type EffectiveChartType = 'line' | 'candles' | 'stacked';

// ─── Resolution ───────────────────────────────────────────────────────────────

export const RESOLUTION_OPTIONS: { value: CandleResolution; label: string }[] =
    [
        { value: 1, label: '1m' },
        { value: 5, label: '5m' },
        { value: 15, label: '15m' },
        { value: 30, label: '30m' },
        { value: 60, label: '1h' },
        { value: 120, label: '2h' },
        { value: 240, label: '4h' },
        { value: 360, label: '6h' },
        { value: 720, label: '12h' },
        { value: 1440, label: '1d' },
        { value: 10080, label: '1w' },
        { value: 43200, label: '1M' },
    ];

const CANDLE_RESOLUTION_STEPS: readonly [number, number][] = [
    [720, 1],
    [1440, 5],
    [4320, 15],
    [10080, 60],
    [43200, 60],
    [86400, 60],
    [129600, 240],
    [259200, 720],
    [525600, 1440],
];

export function autoResolution(range: [Date, Date]): CandleResolution {
    return autoResolutionMinutes(range, CANDLE_RESOLUTION_STEPS, 1440);
}

// ─── Signals ──────────────────────────────────────────────────────────────────

/** Short on-canvas labels for market-level signal pins. */
export const SIGNAL_TYPE_SHORT_MARKET: Record<string, string> = {
    probability_spike: 'spike',
    probability_change: 'prob Δ',
    volume_anomaly: 'vol',
    activity_surge: 'surge',
    buy_sell_imbalance: 'imbal',
    large_trade: 'lg trade',
};

export const CONV_GROUP_COLORS: Record<string, string> = {
    insider: '#f59e0b',
    winner: '#10b981',
    whale: '#6366f1',
};

/** Signal opacity constants. */
export const FULL_LINE = 0.45;
export const FULL_MARKER = 0.9;
export const DIM = 0.06;

// ─── Trade marks ──────────────────────────────────────────────────────────────

/** Marker radius range. Clusters and single trades share it, so a cluster of
 *  small trades cannot outgrow a genuine whale beside it. */
export const MARK_R_MIN = 2.5;
export const MARK_R_MAX = 8;

// ─── Misc ─────────────────────────────────────────────────────────────────────

export const EMPTY_NO_SERIES: ReadonlyMap<string, OHLCVPoint[]> = new Map();
